import React from 'react';
import { FormLabel, Table, Modal } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import PropTypes from 'prop-types';
import ServerSideTableFooter from 'components/common/advance-table/ServerSideTableFooter';
import ErrorFeedback from './ErrorFeedback';

const isMobile = () => window.innerWidth <= 768;

const FormReport = ({
  metadata,
  selectedRows,
  handleSelectRow,
  formValues,
  error,
  onSelectItem
}) => {
  const [page, setPage] = React.useState(0);
  const defaultLimit = metadata?.defaultPageSize || 10;
  const [limit] = React.useState(defaultLimit);

  // Modal state for mobile detail view
  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalRow, setModalRow] = React.useState(null);

  const openModal = row => {
    setModalRow(row);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalRow(null);
  };

  // Get data from metadata options
  const formData = metadata?.options || [];

  const allParentsSelected =
    metadata.dependsOn?.every(
      dep =>
        formValues[dep] !== undefined &&
        formValues[dep] !== null &&
        formValues[dep] !== ''
    ) ?? true;

  // Check individual dependencies
  if (metadata.dependsOn) {
    metadata.dependsOn.forEach(dep => {
      const value = formValues[dep];
    });
  }

  // Simplified render check - only check if dependencies are satisfied and we have data
  if (!allParentsSelected) {
    console.log('❌ FormReport not rendering - dependencies not satisfied');
    return null;
  }

  // Removed early return for empty formData to allow rendering 'No data available' message
  // instead of field being completely hidden.

  function formatLabel(label) {
    let result = label.replaceAll('_', ' ');
    result = result
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    return result;
  }

  // Get all keys except 'id'
  const dataKeys =
    formData.length > 0
      ? Object.keys(formData[0]).filter(key => key !== 'id')
      : [];

  // Paginate data
  const pagedData = formData.slice(page * limit, page * limit + limit);

  // Pagination controls
  const rowCount = formData.length;
  const canPreviousPage = page > 0;
  const canNextPage = (page + 1) * limit < rowCount;

  function cleanValue(val) {
    if (typeof val === 'string') {
      return (
        val
          .replace(/,\s*/g, ', ') // add space after every comma
          .replace(/,\s*$/, '') // remove trailing comma if any
          .trim() || '-'
      );
    }
    return val || '-';
  }

  // Early return if no data keys
  if (dataKeys.length === 0) {
    return (
      <div className="form-report">
        <FormLabel>
          {metadata.label}
          {metadata?.validation?.required && (
            <span className="text-danger ms-1">*</span>
          )}
        </FormLabel>
        <div>No data available to display.</div>
      </div>
    );
  }

  // --- Mobile rendering ---
  if (isMobile()) {
    return (
      <>
        <div className="form-report-mobile">
          <FormLabel>
            {metadata.label}
            {selectedRows.length > 0 && ` (${selectedRows.length} Selected)`}
            {metadata?.validation?.required && (
              <span className="text-danger ms-1">*</span>
            )}
          </FormLabel>
          <ErrorFeedback
            key={metadata.id}
            error={
              metadata?.validation?.required &&
              selectedRows.length === 0 &&
              error
                ? { message: metadata.error_message }
                : null
            }
          />
          {pagedData.map((row, idx) => (
            <div
              key={row.id || idx}
              className="form-report-card"
              style={{
                border: '1px solid #e0e0e0',
                borderRadius: 8,
                marginBottom: 16,
                padding: 16,
                display: 'flex',
                alignItems: 'flex-start',
                boxShadow: '0 1px 4px rgba(0,0,0,0.04)'
              }}
              onClick={() => openModal(row)}
            >
              {!metadata.hideSelect && (
                <input
                  type="checkbox"
                  checked={selectedRows.includes(row['id'])}
                  onClick={e => e.stopPropagation()}
                  // onChange={() => handleSelectRow(metadata, row['id'])}
                  onChange={() => {
                    handleSelectRow(metadata, row['id']);
                    onSelectItem(row);
                  }}
                  style={{ marginRight: 16, marginTop: 4 }}
                />
              )}
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8
                }}
              >
                {dataKeys.map((key, i) => (
                  <div
                    key={key}
                    style={{
                      flex: '1 1 45%',
                      minWidth: 0,
                      textAlign: i % 2 === 0 ? 'left' : 'right',
                      marginBottom: 2
                    }}
                  >
                    <div
                      style={{
                        fontSize: 12,
                        color: '#222',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '100%'
                      }}
                      title={cleanValue(row[key]) || '-'}
                    >
                      {cleanValue(row[key])}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="mt-3">
            <ServerSideTableFooter
              rowCount={rowCount}
              pageSize={limit}
              pageIndex={page}
              setPageSize={() => {}}
              canPreviousPage={canPreviousPage}
              canNextPage={canNextPage}
              nextPage={() => setPage(page + 1)}
              previousPage={() => setPage(Math.max(0, page - 1))}
              navButtons
              onPageChange={setPage}
            />
          </div>
        </div>

        {/* Modal for detail view */}
        <Modal
          show={modalOpen}
          onHide={closeModal}
          size="md"
          centered
          scrollable
        >
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: '16px' }}>
              {metadata.view_modal_title}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {modalRow ? (
              <Table striped bordered hover responsive>
                <tbody>
                  {dataKeys.map(key => (
                    <tr key={key}>
                      <th style={{ fontSize: '12px' }}>{formatLabel(key)}</th>
                      <td style={{ fontSize: '12px' }}>
                        {cleanValue(modalRow[key]) || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <p>No data available.</p>
            )}
          </Modal.Body>
        </Modal>
      </>
    );
  }

  // --- Desktop/table rendering ---
  return (
    <div className="form-report">
      <FormLabel>
        {metadata.label}
        {selectedRows.length > 0 && ` (${selectedRows.length} Selected)`}
        {metadata?.validation?.required && (
          <span className="text-danger ms-1">*</span>
        )}
      </FormLabel>
      <ErrorFeedback
        key={metadata.id}
        error={
          metadata?.validation?.required && selectedRows.length === 0 && error
            ? { message: metadata.error_message }
            : null
        }
      />
      <Table striped bordered hover responsive className="w-100">
        <thead>
          <tr>
            {pagedData.length > 0 && !metadata.hideSelect && <th>Select</th>}
            {dataKeys.map(key => (
              <th key={key}>{formatLabel(key)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {pagedData.length > 0 ? (
            pagedData.map((row, index) => (
              <tr key={row.id || index}>
                {!metadata.hideSelect && (
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedRows.includes(row['id'])}
                      // onChange={() => handleSelectRow(metadata, row['id'])}
                      onChange={() => {
                        handleSelectRow(metadata, row['id']);
                        onSelectItem(row);
                      }}
                    />
                  </td>
                )}
                {dataKeys.map(key => (
                  <td key={key}>{cleanValue(row[key]) || '-'}</td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={dataKeys.length + 1}>No data available</td>
            </tr>
          )}
        </tbody>
      </Table>
      <div className="mt-3">
        <ServerSideTableFooter
          rowCount={rowCount}
          pageSize={limit}
          pageIndex={page}
          setPageSize={() => {}}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          nextPage={() => setPage(page + 1)}
          previousPage={() => setPage(Math.max(0, page - 1))}
          navButtons
          onPageChange={setPage}
        />
      </div>
    </div>
  );
};

FormReport.defaultProps = {
  metadata: {
    options: [],
    defaultPageSize: 10
  },
  selectedRows: [],
  handleSelectRow: () => {}
};

FormReport.propTypes = {
  metadata: PropTypes.shape({
    options: PropTypes.arrayOf(PropTypes.object),
    label: PropTypes.string,
    dependsOn: PropTypes.arrayOf(PropTypes.string),
    defaultPageSize: PropTypes.number,
    view_modal_title: PropTypes.string,
    validation: PropTypes.shape({
      required: PropTypes.bool
    }),
    id: PropTypes.string,
    error_message: PropTypes.string,
    hideSelect: PropTypes.bool
  }),
  selectedRows: PropTypes.arrayOf(PropTypes.string),
  handleSelectRow: PropTypes.func,
  formValues: PropTypes.object,
  error: PropTypes.string,
  onSelectItem: PropTypes.func
};

export default FormReport;
